import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListSubheader,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Voicemail as VoicemailIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  HeadsetMic as HeadsetMicIcon,
  EmailOutlined as EmailOutlinedIcon,
  Link as LinkIcon,
  AccountTree as FunnelIcon,
} from '@mui/icons-material';
import { SupportInteraction, EmailAccountStatus, FilterType, SupportFunnel } from './types';
import { timeAgo, displayName } from './utils';
import { apiService } from '../../services/api';

interface MessageListProps {
  items: SupportInteraction[];
  loading: boolean;
  selectedItem: SupportInteraction | null;
  onSelectItem: (item: SupportInteraction) => void;
  filter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  search: string;
  onSearchChange: (search: string) => void;
  onSearchSubmit: () => void;
  onRefresh: () => void;
  stats: { unreadCount: number } | null;
  accounts: EmailAccountStatus[];
  isEmailConfigured: boolean;
  onConnectEmail: () => void;
}

const MessageList: React.FC<MessageListProps> = ({
  items,
  loading,
  selectedItem,
  onSelectItem,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  onSearchSubmit,
  onRefresh,
  stats,
  accounts,
  isEmailConfigured,
  onConnectEmail,
}) => {
  // Build a color map from accounts
  const accountColorMap: Record<string, string> = {};
  for (const acc of accounts) {
    accountColorMap[acc.id] = acc.color;
  }

  // Hover quick-action state
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Right-click context menu
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    item: SupportInteraction;
  } | null>(null);

  // Funnels — loaded lazily on first context menu open
  const [funnels, setFunnels] = useState<SupportFunnel[]>([]);
  const funnelsLoaded = useRef(false);
  const [funnelsLoading, setFunnelsLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const loadFunnels = useCallback(async () => {
    if (funnelsLoaded.current) return;
    setFunnelsLoading(true);
    try {
      const data = await apiService.getSupportFunnels();
      setFunnels((data as SupportFunnel[]).filter(f => f.is_active));
      funnelsLoaded.current = true;
    } catch {
      // non-critical
    } finally {
      setFunnelsLoading(false);
    }
  }, []);

  const openContextMenu = (e: React.MouseEvent, item: SupportInteraction) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, item });
    loadFunnels();
  };

  const closeContextMenu = () => setContextMenu(null);

  const handleAssign = async (funnelId: string, stageId: string, funnelName: string, stageName: string) => {
    if (!contextMenu) return;
    const { item } = contextMenu;
    closeContextMenu();
    if (item.type === 'voicemail') {
      setSnackbar({ msg: 'Funnel assignment is only available for emails', severity: 'error' });
      return;
    }
    setAssigning(true);
    try {
      await apiService.assignInteractionToFunnel(item.external_id, { funnelId, stageId });
      setSnackbar({ msg: `Moved to "${funnelName} → ${stageName}"`, severity: 'success' });
    } catch {
      setSnackbar({ msg: 'Failed to assign to funnel — please try again', severity: 'error' });
    } finally {
      setAssigning(false);
    }
  };

  const handleQuickFunnel = (e: React.MouseEvent, item: SupportInteraction) => {
    e.stopPropagation();
    // Reuse context menu anchored to the button position
    openContextMenu(e, item);
  };

  return (
    <Box sx={{ width: 380, minWidth: 380, display: 'flex', flexDirection: 'column', borderRight: '1px solid', borderColor: 'divider' }}>
      {/* Search + Refresh */}
      <Box sx={{ p: 1.5, display: 'flex', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Search messages..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearchSubmit()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <Tooltip title="Refresh inbox">
          <IconButton onClick={onRefresh} color="primary" size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, val) => val && onFilterChange(val as FilterType)}
          size="small"
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: '0.75rem' } }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="unread">
            <Badge badgeContent={stats?.unreadCount || 0} color="error" max={99}>
              Unread
            </Badge>
          </ToggleButton>
          <ToggleButton value="unresolved">Open</ToggleButton>
          <ToggleButton value="voicemail">VM</ToggleButton>
          <ToggleButton value="email">Email</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Message List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            {!isEmailConfigured ? (
              <>
                <EmailOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontSize: '1rem' }}>
                  No email connected
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                  Connect your support email to start receiving messages.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={onConnectEmail}
                  sx={{ bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}
                >
                  Connect Email
                </Button>
              </>
            ) : (
              <>
                <HeadsetMicIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary" variant="body2">No messages found</Typography>
                <Typography variant="caption" color="text.disabled">
                  {filter !== 'all' ? 'Try changing your filter' : 'Inbox is empty'}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <List disablePadding>
            {items.map((item) => {
              const accountColor = item.email_account_id ? accountColorMap[item.email_account_id] : undefined;
              const isHovered = hoveredId === item.external_id;

              return (
                <ListItem
                  key={item.external_id}
                  button
                  onClick={() => onSelectItem(item)}
                  onContextMenu={(e) => openContextMenu(e, item)}
                  onMouseEnter={() => setHoveredId(item.external_id)}
                  onMouseLeave={() => setHoveredId(null)}
                  selected={selectedItem?.external_id === item.external_id}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: !item.is_read ? 'action.hover' : 'transparent',
                    pl: 0,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                      '& .MuiTypography-root': { color: 'primary.contrastText' },
                    },
                  }}
                >
                  {/* Color strip */}
                  <Box
                    sx={{
                      width: 4,
                      alignSelf: 'stretch',
                      bgcolor: accountColor || '#9CA3AF',
                      borderRadius: '0 2px 2px 0',
                      flexShrink: 0,
                      mr: 1,
                    }}
                  />
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    {item.type === 'voicemail' ? (
                      <VoicemailIcon fontSize="small" color={item.is_read ? 'disabled' : 'warning'} />
                    ) : (
                      <EmailIcon fontSize="small" color={item.is_read ? 'disabled' : 'info'} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: item.is_read ? 400 : 700,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.82rem',
                          }}
                        >
                          {displayName(item.from_address)}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7, flexShrink: 0, fontSize: '0.68rem' }}>
                          {timeAgo(item.received_at)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontWeight: item.is_read ? 400 : 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.subject || '(No subject)'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
                          {item.is_resolved && (
                            <Chip label="Resolved" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                          {item.callback_initiated && (
                            <Chip label="Called back" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                          )}
                          {item.member_name && (
                            <Chip
                              icon={<PersonIcon sx={{ fontSize: '0.75rem !important' }} />}
                              label={item.member_name}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                          {/* Hover quick-action: add to funnel */}
                          {isHovered && item.type === 'email' && (
                            <Tooltip title="Add to funnel (or right-click)" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={(e) => handleQuickFunnel(e, item)}
                                sx={{
                                  ml: 'auto',
                                  p: 0.25,
                                  color: '#1A5F7A',
                                  bgcolor: '#1A5F7A14',
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: '#1A5F7A25' },
                                  flexShrink: 0,
                                }}
                              >
                                <FunnelIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Right-click / quick-action context menu */}
      <Menu
        open={!!contextMenu}
        onClose={closeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
        }
        PaperProps={{
          sx: {
            minWidth: 240,
            maxWidth: 300,
            maxHeight: 400,
            overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#1A5F7A', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.65rem' }}>
            Add to Funnel
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.68rem', mt: 0.25 }} noWrap>
            {contextMenu?.item.subject || contextMenu?.item.from_address}
          </Typography>
        </Box>

        {funnelsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : funnels.length === 0 ? (
          <MenuItem disabled sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
            No active funnels — create one first
          </MenuItem>
        ) : (
          funnels.map((funnel) => [
            <ListSubheader
              key={`header-${funnel.id}`}
              sx={{
                lineHeight: '28px',
                fontSize: '0.72rem',
                fontWeight: 700,
                color: funnel.color || '#1A5F7A',
                bgcolor: `${funnel.color || '#1A5F7A'}0f`,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: funnel.color || '#1A5F7A', flexShrink: 0 }} />
              {funnel.name}
            </ListSubheader>,
            ...(funnel.stages || []).map((stage) => (
              <MenuItem
                key={`stage-${stage.id}`}
                onClick={() => handleAssign(funnel.id, stage.id, funnel.name, stage.name)}
                sx={{
                  pl: 4,
                  fontSize: '0.8rem',
                  py: 0.75,
                  '&:hover': { bgcolor: `${funnel.color || '#1A5F7A'}14` },
                }}
              >
                <Box
                  sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: stage.color || funnel.color || '#1A5F7A', mr: 1, flexShrink: 0 }}
                />
                {stage.name}
              </MenuItem>
            )),
          ])
        )}
      </Menu>

      {/* Assignment feedback */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3500}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          sx={{ width: '100%', fontSize: '0.82rem' }}
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageList;
